import {
  Children,
  type HTMLAttributes,
  type KeyboardEvent,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  cloneElement,
  isValidElement,
  useCallback,
  useId,
  useState,
} from "react";

import { cx, styled } from "simplydo/react/styled";

import { Menu } from "../menu/Menu";
import type { MenuItemProps } from "../menu/Menu.types";
import { MenuItem } from "../menu/MenuItem";
import { isElementOfType } from "../utils/elementType";
import { StyledTab } from "./Tab.styles";
import type { TabPaneProps, TabPaneShorthand, TabProps } from "./Tab.types";
import { TabPane } from "./TabPane";

function toIndex(value: number | string | undefined, fallback = 0): number {
  if (value === undefined) return fallback;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    !isValidElement(value)
  );
}

type TabAttributes = HTMLAttributes<HTMLElement> & { id: string };

function isTabEnabled(menuItem: TabPaneShorthand["menuItem"]): boolean {
  if (menuItem == null) return false;
  if (isValidElement(menuItem)) {
    return !(menuItem.props as MenuItemProps).disabled;
  }
  if (isPlainObject(menuItem)) {
    return !(menuItem as MenuItemProps).disabled;
  }
  return true;
}

function resolveMenuItem(
  menuItem: TabPaneShorthand["menuItem"],
  index: number,
  active: boolean,
  tabProps: TabAttributes,
  onSelect: (event: MouseEvent<HTMLElement>, index: number) => void,
): ReactNode {
  if (menuItem == null) {
    return null;
  }

  if (typeof menuItem === "string" || typeof menuItem === "number") {
    return (
      <MenuItem
        key={index}
        active={active}
        {...tabProps}
        onClick={(e) => onSelect(e, index)}
      >
        {menuItem}
      </MenuItem>
    );
  }

  if (isValidElement(menuItem)) {
    const existingOnClick = (menuItem.props as MenuItemProps).onClick;
    return cloneElement(menuItem as ReactElement<MenuItemProps>, {
      key: menuItem.key ?? index,
      active,
      ...tabProps,
      onClick: (event: MouseEvent<HTMLElement>) => {
        existingOnClick?.(event);
        onSelect(event, index);
      },
    });
  }

  if (isPlainObject(menuItem)) {
    return (
      <MenuItem
        key={index}
        active={active}
        {...(menuItem as MenuItemProps)}
        {...tabProps}
        onClick={(e) => {
          (menuItem as MenuItemProps).onClick?.(e);
          onSelect(e, index);
        }}
      />
    );
  }

  return (
    <MenuItem
      key={index}
      active={active}
      {...tabProps}
      onClick={(e) => onSelect(e, index)}
    >
      {menuItem as ReactNode}
    </MenuItem>
  );
}

const TAB_KEYS = [
  "ArrowRight",
  "ArrowDown",
  "ArrowLeft",
  "ArrowUp",
  "Home",
  "End",
];

const handleTabListKeyDown = (event: KeyboardEvent<HTMLElement>) => {
  if (!TAB_KEYS.includes(event.key)) {
    return;
  }
  const tabs = Array.from(
    event.currentTarget.querySelectorAll<HTMLElement>(
      '[role="tab"]:not(.is-disabled)',
    ),
  );
  const current = tabs.indexOf(document.activeElement as HTMLElement);
  if (current < 0) {
    return;
  }
  const last = tabs.length - 1;
  const next =
    event.key === "Home"
      ? 0
      : event.key === "End"
        ? last
        : event.key === "ArrowRight" || event.key === "ArrowDown"
          ? current === last
            ? 0
            : current + 1
          : current === 0
            ? last
            : current - 1;
  event.preventDefault();
  tabs[next]?.focus();
  tabs[next]?.click();
};

const TabComponent = (props: TabProps): ReactElement => {
  const {
    activeIndex: activeIndexProp,
    defaultActiveIndex = 0,
    menu,
    variant,
    panes,
    renderActiveOnly = true,
    onTabChange,
    children,
    className,
    style,
  } = props;

  const baseId = useId();
  const tabId = (index: number) => `${baseId}tab-${index}`;
  const panelId = (index: number) => `${baseId}panel-${index}`;

  const isControlled = activeIndexProp !== undefined;
  const [uncontrolledIndex, setUncontrolledIndex] = useState(() =>
    toIndex(defaultActiveIndex, 0),
  );
  const activeIndex = isControlled
    ? toIndex(activeIndexProp, 0)
    : uncontrolledIndex;

  const handleSelect = useCallback(
    (event: MouseEvent<HTMLElement>, index: number) => {
      if (!isControlled) {
        setUncontrolledIndex(index);
      }
      onTabChange?.(event, { ...props, activeIndex: index });
    },
    [isControlled, onTabChange, props],
  );

  if (panes) {
    const tabStop = isTabEnabled(panes[activeIndex]?.menuItem)
      ? activeIndex
      : panes.findIndex((pane) => isTabEnabled(pane.menuItem));
    const menuItems = panes.map((pane, index) => {
      const isActive = activeIndex === index;
      const isEnabled = isTabEnabled(pane.menuItem);
      return resolveMenuItem(
        pane.menuItem,
        index,
        isActive,
        {
          id: tabId(index),
          role: "tab",
          "aria-selected": isActive,
          "aria-controls": isActive ? panelId(index) : undefined,
          "aria-disabled": isEnabled ? undefined : true,
          tabIndex: index === tabStop ? 0 : -1,
        },
        handleSelect,
      );
    });

    const paneNodes = panes.map((pane, index) => {
      const isActive = activeIndex === index;
      if (renderActiveOnly && !isActive) {
        return null;
      }

      let content: ReactNode = null;
      if (pane.render) {
        content = pane.render();
      } else if (pane.pane != null) {
        if (isValidElement(pane.pane)) {
          content = pane.pane;
        } else if (isPlainObject(pane.pane)) {
          content = <TabPane {...(pane.pane as object)} active={isActive} />;
        } else {
          content = (
            <TabPane active={isActive}>{pane.pane as ReactNode}</TabPane>
          );
        }
      }

      const panelProps = {
        id: panelId(index),
        role: "tabpanel",
        "aria-labelledby": tabId(index),
      };

      if (isElementOfType(content, TabPane)) {
        return cloneElement(content as ReactElement<TabPaneProps>, {
          key: index,
          ...panelProps,
          ...(renderActiveOnly ? { active: true } : null),
        });
      }

      if (content == null || !isActive) {
        return content;
      }

      return (
        <div key={index} {...panelProps} style={{ display: "contents" }}>
          {content}
        </div>
      );
    });

    return (
      <StyledTab className={cx(className)} style={style}>
        <Menu
          {...menu}
          variant={variant ?? menu?.variant ?? "tabular"}
          role="tablist"
          onKeyDown={(event) => {
            menu?.onKeyDown?.(event);
            handleTabListKeyDown(event);
          }}
        >
          {menuItems}
        </Menu>
        {paneNodes}
      </StyledTab>
    );
  }

  // Children mode: Tab.Pane children; optional first-child Menu not used —
  // callers typically use panes. Support Pane children with activeIndex.
  const childArray = Children.toArray(children);
  return (
    <StyledTab className={cx(className)} style={style}>
      {childArray.map((child, index) => {
        if (!isValidElement(child)) return child;
        if (isElementOfType(child, TabPane)) {
          const isActive = activeIndex === index;
          if (renderActiveOnly && !isActive) return null;
          return cloneElement(child as ReactElement<{ active?: boolean }>, {
            key: child.key ?? index,
            active: isActive,
          });
        }
        return child;
      })}
    </StyledTab>
  );
};

export const Tab = styled(TabComponent)``;
